<script setup lang="ts">
import { isNumber } from 'lodash-es';
import {
  computed,
  onMounted,
  ref,
  toRef,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { IApiErrorField, IApiFormErrors } from '@modules/core/services/query/types';
import type {
  FormFlattenKey,
  FormFlattenValue,
  FormResult,
} from '@modules/forms/components/VForm/types';
import type {
  IFormSelectInputOption,
} from '@modules/forms/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import {
  ADMIN_AVAILABLE_PAYMENT_SYSTEMS_LIST_ROUTE_NAME,
} from '@modules/available-payment-systems/constants';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useMerchantIdStore } from '@modules/merchant/store';
import { watchImmediate } from '@vueuse/core';

import type {
  IAdminSettingsPaymentCountryGroupItemCountry,
  IAdminSettingsPaymentCountryGroupItemFields,
  IAdminSettingsPaymentCountryGroupItemPayload,
  IAdminSettingsPaymentCountryGroupItemResponse,
  IAdminSettingsPaymentCountryGroupItemSavePayload,
  IIAdminSettingsPaymentCountryGroupItemFormSchema,
} from '../../types';
import PaymentCountryGroupsFormConfig from '../../components/PaymentCountryGroupsFormConfig/PaymentCountryGroupsFormConfig.vue';
import { TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS } from '../../constants';
import { useSettingsPaymentCountryGroupSaveMutation } from '../../mutations';
import { useSettingsPaymentCountryGroupsItemQuery } from '../../queries';

const route = useRoute();
const router = useRouter();
const { getTranslation } = useI18n();
const merchantId = toRef(useMerchantIdStore(), 'merchantId');
const isCreateStateInitially = !route.params.id;
const itemPayload = computed<IAdminSettingsPaymentCountryGroupItemPayload>(() => ({ id: Number.parseInt(route.params.id ? route.params.id.toString() : '0', 10) }));
const {
  data: initialData,
  isLoading: isLoadingItem,
  refetch,
} = useSettingsPaymentCountryGroupsItemQuery(itemPayload);
const {
  data: updatedData,
  isLoading: isLoadingSubmission,
  mutateAsync,
  errors: rawErrors,
  clearError,
} = useSettingsPaymentCountryGroupSaveMutation();

const data = computed<IAdminSettingsPaymentCountryGroupItemResponse | undefined>(() => (updatedData.value ?? initialData.value));

const formPayload = ref<IAdminSettingsPaymentCountryGroupItemFields>();

const isLoading = computed<boolean>(() => (isCreateStateInitially && router.currentRoute.value.params.id ? false : isLoadingItem.value));

onMounted(() => {
  void refetch();
});

const countriesErrorNames: string[] = [
  'payment_system_countries.name',
  'payment_system_countries.country_code',
  'payment_system_countries.country_group_id',
];

const formErrors = computed<IApiFormErrors | undefined>(() => {
  let countryErrors: IApiErrorField[] = [];
  if (rawErrors.value && Object.keys(rawErrors.value).some((a) => countriesErrorNames.includes(a))) {
    for (const name of countriesErrorNames) {
      const errL = rawErrors.value[name];
      if (errL) {
        countryErrors = countryErrors.concat(errL);
      }
    }
  }

  if (rawErrors.value?.countries) {
    countryErrors = countryErrors.concat(rawErrors.value?.countries);
  }

  return { ...rawErrors.value, countries: countryErrors };
});

watchImmediate(data, (newValue) => {
  if (newValue) {
    formPayload.value = {
      name: newValue.name ? newValue.name : '',
      countries: newValue.countries.filter((a) => a.check).map((a) => a.country_code),
      validation_data: newValue.bank_transfer_validation.validation_data,
    };
  }
});

const countriesOptions = computed<IFormSelectInputOption<number, 'country_code', 'name'>[]>(() => data.value?.countries ?? []);

const formSchema = computed<IIAdminSettingsPaymentCountryGroupItemFormSchema>(() => ({
  name: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS, 'GROUP_NAME').value,
    value: formPayload.value?.name || undefined,
    required: true,
  },
  countries: {
    type: EFormInputTypes.MULTISELECT_BOX,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS, 'COUNTRIES_LIST').value,
    options: countriesOptions.value || [],
    optionValue: 'country_code',
    optionLabel: 'name',
    value: formPayload.value?.countries ?? [],
  },
  validation_data: {
    type: EFormInputTypes.SLOT,
    value: formPayload.value?.validation_data,
  },
}));

function handleFormChange<
  T extends IAdminSettingsPaymentCountryGroupItemFields,
  K extends FormFlattenKey<T, 'validation_data'>,
>(key: K, value: FormFlattenValue<T, K> | undefined, { values }: FormResult<T>): void {
  formPayload.value = values;
}

function handleFormSubmit(rawPayload: IAdminSettingsPaymentCountryGroupItemFields): void {
  const itemId = itemPayload.value?.id;
  const countriesToSubmit: IAdminSettingsPaymentCountryGroupItemCountry[] = data.value?.countries.map(
    (a) => ({ ...a, check: !!rawPayload.countries?.includes(a.country_code) }),
  ) ?? [];

  const payload: IAdminSettingsPaymentCountryGroupItemSavePayload = {
    id: isNumber(itemId) && itemId !== 0 ? itemPayload.value.id : undefined,
    merchant: merchantId.value,
    payment_system_country_group: {
      name: rawPayload.name,
      bank_transfer_validation_attributes: {
        id: data.value?.bank_transfer_validation.id ? data.value?.bank_transfer_validation.id : null,
        validation_data: rawPayload.validation_data,
      },
      payment_system_countries_attributes: countriesToSubmit,
    },
  };

  void mutateAsync(payload).then((res) => {
    if (!itemId) {
      void router.replace({
        name: route.name,
        params: {
          id: res.id,
        },
      });
    }
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'PaymentCountryGroupsFormPage'"
    :title="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS, 'HEADER')"
    type="form"
  >
    <VLoader :loading="isLoading">
      <VForm
        :schema="formSchema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS, 'SAVE_BUTTON')"
        :is-loading="isLoadingSubmission"
        :custom-errors="formErrors"
        @interacted="clearError"
        @change="handleFormChange"
        @submit="handleFormSubmit"
      >
        <template #validation_data="{ className, errors, readonly, onChange, onBlur }">
          <PaymentCountryGroupsFormConfig
            :data="formPayload?.validation_data"
            :errors="errors"
            :readonly="readonly"
            :class="`${className} !mb-8`"
            @change="onChange('validation_data', $event)"
            @blur="onBlur('validation_data', $event)"
          />
        </template>
        <template #secondaryBtn>
          <VBackButton
            :label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS, 'BACK_BUTTON')"
            :to="{ name: ADMIN_AVAILABLE_PAYMENT_SYSTEMS_LIST_ROUTE_NAME }"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
